import type { ApiErrorDetail, ValidatedBasketLine } from '@optiflow/types';

/**
 * Pairs every basket line with its own result from a basket check, by the line key the check echoes back — never by position, never by frame.
 *
 * Returns null, and pairs nothing, unless the answer is exactly one result per line: a result without a key, with a key the basket never sent, with a key seen twice, or for a different frame than its line, and a line left without a result, all mean the answer cannot be matched safely. A null answer is handled as a failed check, so the saved basket stays protected instead of being rebuilt from a guess.
 */
export function matchResultsByLineKey(
  lines: readonly { lineId: string; variantId: string }[],
  results: readonly ValidatedBasketLine[],
): Map<string, ValidatedBasketLine> | null {
  const expected = new Map(lines.map((line) => [line.lineId, line]));
  const matched = new Map<string, ValidatedBasketLine>();

  for (const result of results) {
    const key = result.lineKey;
    if (typeof key !== 'string') return null;
    const line = expected.get(key);
    if (!line || matched.has(key)) return null;
    if (result.variantId.toLowerCase() !== line.variantId.toLowerCase()) return null;
    matched.set(key, result);
  }

  return matched.size === expected.size ? matched : null;
}

const REFUSED_TOKEN_FIELD = /^lines\.(\d+)\.recommendationToken$/;

/**
 * The basket lines a refused checkout named, as line ids.
 *
 * The server names each refused line by its position in the request, so it is read against the ids of the lines exactly as they were sent. A detail that names no such line is ignored.
 */
export function refusedRecommendationLineIds(
  details: readonly ApiErrorDetail[],
  submittedLineIds: readonly string[],
): string[] {
  return details.flatMap((detail) => {
    const match = REFUSED_TOKEN_FIELD.exec(detail.field ?? '');
    const lineId = match ? submittedLineIds[Number(match[1])] : undefined;
    return lineId ? [lineId] : [];
  });
}
